import { PointVideoHistoryQuerySort, usePointVideoHistoryQuery } from '@shared/api/graphql/generated';
import { FetchPolicyKeys } from '@shared/api/graphql/types';
import { useEffect, useRef, useState } from 'react';

const PAGE_SIZE = 50;

const useVideoHistory = (pointId: string, dateFrom: string, dateTo: string) => {
  const scope = JSON.stringify([pointId, dateFrom, dateTo]);
  const currentScope = useRef(scope);
  const generation = useRef(0);
  const pending = useRef<{ generation: number } | undefined>(undefined);
  const [paginationError, setPaginationError] = useState<unknown>();
  if (currentScope.current !== scope) {
    currentScope.current = scope;
    generation.current += 1;
    pending.current = undefined;
  }
  useEffect(() => {
    setPaginationError(undefined);
  }, [scope]);
  useEffect(
    () => () => {
      generation.current += 1;
      pending.current = undefined;
    },
    []
  );
  const { data, loading, error, refetch, fetchMore } = usePointVideoHistoryQuery({
    fetchPolicy: FetchPolicyKeys.NETWORK_ONLY,
    notifyOnNetworkStatusChange: true,
    context: { queryDeduplication: false },
    skip: !pointId || !dateFrom || !dateTo,
    variables: {
      filters: { pointId, onAirRange: { dateFrom, dateTo } },
      pagination: { page: 0, pageSize: PAGE_SIZE },
      sort: PointVideoHistoryQuerySort.OnAirDesc,
    },
  });

  const fetchMoreVideoHistory = async () => {
    const pagination = data?.pointVideoHistoryPaginationQuery.paginationInfo;
    if (!pointId || !dateFrom || !dateTo || !pagination?.hasNextPage || loading || pending.current) return;
    const request = { generation: generation.current };
    pending.current = request;
    setPaginationError(undefined);
    try {
      await fetchMore({
        updateQuery: (prev, { fetchMoreResult }) => {
          if (generation.current !== request.generation || pending.current !== request) return prev;
          const previousIds = new Set((prev.pointVideoHistoryPaginationQuery.result ?? []).map((item) => item.id));
          const data = {
            ...prev,
            pointVideoHistoryPaginationQuery: {
              ...prev?.pointVideoHistoryPaginationQuery,
              ...fetchMoreResult?.pointVideoHistoryPaginationQuery,
              result: [
                ...(prev?.pointVideoHistoryPaginationQuery?.result || []),
                ...(fetchMoreResult?.pointVideoHistoryPaginationQuery?.result || []).filter(
                  (item) => !previousIds.has(item.id)
                ),
              ],
              paginationInfo: {
                ...prev.pointVideoHistoryPaginationQuery.paginationInfo,
                ...fetchMoreResult.pointVideoHistoryPaginationQuery.paginationInfo,
              },
            },
          };

          return data;
        },
        variables: {
          filters: { pointId, onAirRange: { dateFrom, dateTo } },
          pagination: { page: pagination.page + 1, pageSize: pagination.pageSize },
          sort: PointVideoHistoryQuerySort.OnAirDesc,
        },
      });
    } catch (cause: unknown) {
      if (generation.current === request.generation && pending.current === request) setPaginationError(cause);
    } finally {
      if (pending.current === request) pending.current = undefined;
    }
  };

  const refetchVideoHistory: typeof refetch = async (...args) => {
    generation.current += 1;
    const request = { generation: generation.current };
    pending.current = request;
    setPaginationError(undefined);
    try {
      return await refetch(...args);
    } finally {
      if (pending.current === request) pending.current = undefined;
    }
  };

  return {
    error: error ?? paginationError,
    loading,
    fetchMoreVideoHistory,
    refetch: refetchVideoHistory,
    videoHistory: data?.pointVideoHistoryPaginationQuery.result,
    videoHistoryPaginationInfo: data?.pointVideoHistoryPaginationQuery.paginationInfo,
  };
};

export default useVideoHistory;
